'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { Code, GitBranch, List, Sparkles } from 'lucide-react'
import { cn } from '../../../lib/cn'
import {
  sectionVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../../lib/motion'

interface FeatureItem {
  icon: ReactNode
  title: string
  /** Feature description - supports Markdown (bold, code, links) */
  description: string
}

/**
 * FeaturesRow - Horizontal row of features with icon + title + description
 *
 * **Layout:**
 * ```
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ [icon] Title    │ [icon] Title    │ [icon] Title    │ [icon] Title │
 * │ Description...  │ Description...  │ Description...  │ Description..│
 * └──────────────────────────────────────────────────────────────────┘
 * │                        border-b                                   │
 * ```
 *
 * **CONSTRAINTS:**
 * - Simple horizontal layout with icon + title + description
 * - Supports any number of features (3, 4, 6, etc.)
 * - Responsive: 1 col mobile, 2 cols tablet, configurable desktop columns
 * - Border bottom for visual separation
 * - No header section - just the feature items
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Icons should be consistent size (h-5 w-5)
 * - Best with 3, 4, or 6 items for balanced layout
 * - Columns prop controls desktop layout (3 or 4 columns)
 */
export interface FeaturesRowProps {
  /** Feature items with icon, title, and description */
  features?: FeatureItem[]
  /** Number of columns on desktop (default: 4) */
  columns?: 3 | 4
  className?: string
}

const defaultFeatures: FeatureItem[] = [
  {
    icon: <Code className="size-5" />,
    title: 'Type-safe Components',
    description: 'Build with confidence using fully typed React components that catch errors at compile time.',
  },
  {
    icon: <GitBranch className="size-5" />,
    title: 'Version Control',
    description: 'Track changes and collaborate seamlessly with built-in git-friendly architecture.',
  },
  {
    icon: <List className="size-5" />,
    title: 'Task Management',
    description: 'Organize your workflow with integrated issue tracking and project management.',
  },
  {
    icon: <Sparkles className="size-5" />,
    title: 'Production Ready',
    description: 'Ship faster with components that are tested, accessible, and optimized for performance.',
  },
]

const FeaturesRow = ({
  features = defaultFeatures,
  columns = 4,
  className,
}: FeaturesRowProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-8 sm:py-16', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <motion.div
          className="flex flex-wrap items-start justify-between py-0 sm:py-24"
          variants={gridContainerVariants(0.1, 0.1)}
        >
          {features.map((item) => (
            <motion.div
              key={item.title}
              className={cn(
                'flex shrink grow basis-full flex-col items-start justify-between py-6 sm:p-6 md:basis-1/2',
                columns === 3 ? 'lg:basis-1/3' : 'lg:basis-1/4'
              )}
              variants={itemAnimation}
            >
              <div className="mb-3 flex text-foreground items-center justify-start gap-3">
                {item.icon}
                <h3 className="text-lg font-semibold">{item.title}</h3>
              </div>
              <div className="prose prose-sm max-w-none text-balance text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                <Markdown>{item.description}</Markdown>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { FeaturesRow }
